import { SpaceController } from "features/virtual-space/useSpacePlayerController";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { RoomCollection } from "@lifeat/shared/data-models/Rooms/RoomEntity";
import { VirtualSpace } from "@lifeat/shared/data-models/virtual-space/VirtualSpace";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import { useDatabaseContext } from "@contexts/DatabaseContext";
import { useUserContext } from "@contexts/UserContext";
import { useEvent } from "@hooks/useEvent";
import * as RoomService from "../RoomService";

/**
 * This hook handles synchronizing host & guest spaces in a room when `isSpaceSynced` option is enabled.
 */
export function useRoomSpaceSync(spaceController: SpaceController) {
    const [isSpaceSyncEnabled, setSpaceSyncEnabled] = useState(false);
    const [hostSpaceId, setHostSpaceId] = useState<string | null>(null);
    const db = useDatabaseContext();
    const params = useParams<{ roomId?: string }>();
    const { userData } = useUserContext();

    const roomId = useMemo(() => {
        return (params.roomId || userData?.roomsData?.personalRoomId) ?? null;
    }, [params.roomId, userData?.roomsData?.personalRoomId]);

    const isHost = isRoomOwnedByUser(roomId, userData);
    const shouldSubscribeToHostSpace = !isHost && isSpaceSyncEnabled;
    const shouldPublishSpaceToGuests = isHost && isSpaceSyncEnabled;

    const spaceToBePlayed = useMemo(() => {
        if (shouldSubscribeToHostSpace && hostSpaceId) {
            return spaceController.getSpaceById(hostSpaceId);
        } else {
            return null;
        }
    }, [hostSpaceId, shouldSubscribeToHostSpace, spaceController]);

    const subscribeSpaceSynced = useEvent((roomId: string) => {
        const unsubscribe = db.subscribe(RoomCollection.at(roomId).at("isSpaceSynced").node, "value", (e) => {
            if (e.success) {
                setSpaceSyncEnabled(e.value);
            }
        });
        return unsubscribe;
    });

    const subscribeRoomSpaceId = useEvent((roomId: string) => {
        const unsubscribe = db.subscribe(RoomCollection.at(roomId).at("spaceId").node, "value", (e) => {
            if (e.success) {
                setHostSpaceId(e.value);
            }
        });
        return unsubscribe;
    });

    const publishSpaceToGuests = useEvent((roomId: string, spaceId: string) => {
        RoomService.setRoomSpaceId(roomId, spaceId);
    });

    const onHostSpaceChange = useEvent((space: VirtualSpace) => {
        if (shouldSubscribeToHostSpace) {
            spaceController.playSpace(space);
        }
    });

    useEffect(() => {
        if (roomId == null) {
            setSpaceSyncEnabled(false);
        } else {
            return subscribeSpaceSynced(roomId);
        }
    }, [subscribeSpaceSynced, roomId]);

    useEffect(() => {
        if (shouldSubscribeToHostSpace) {
            return subscribeRoomSpaceId(roomId);
        }
    }, [roomId, shouldSubscribeToHostSpace, subscribeRoomSpaceId]);

    useEffect(() => {
        if (shouldPublishSpaceToGuests && spaceController.selectedSpace) {
            publishSpaceToGuests(roomId, spaceController.selectedSpace.id);
        }
    }, [roomId, shouldPublishSpaceToGuests, spaceController.selectedSpace, publishSpaceToGuests]);

    useEffect(() => {
        if (spaceToBePlayed) {
            onHostSpaceChange(spaceToBePlayed);
        }
    }, [spaceToBePlayed, onHostSpaceChange]);
}
